import { LoadingButton } from "@mui/lab"
import { Box, Hidden, IconButton } from "@mui/material"
import { useMutation } from "@tanstack/react-query"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"
import { ActionDrawer } from "../../../../../../components/ActionDrawer"
import { CustomePanel, CustomeTab } from "../../../../../../components/customeTab"
import Loader from "../../../../../../components/loader"
import StatusComponent from "../../../../../../components/status"
import { emitEvent } from "../../../../../../hooks/emitter"
import { EventQuery, Events, queryClient } from "../../../../../../hooks/emitter/types"
import { getStatus } from "../../../../../../hooks/status"
import { useContextState } from "../../../../../../hooks/theme"
import { userScheduleServiceFetch } from "../../../../../../utils/constants/callService"
import { Status, Types } from "../../../../../../utils/enums"
import { TypeOfQuestionarie } from "../../../../../../utils/enums/typeQuestionarie"
import { icons } from "../../../../../../utils/icons"
import { BosySecton } from "../bodySection"
import { Section } from "../section"
import Visite from "../visite"
import { useStyles } from "./styles"
import { DetailVisitProps } from "./types"

const DetailVisit = ({ data, bodySection, isStore, questionsData, visitList = [], saveReportUrl, seeDetail }: DetailVisitProps) => {
    const { id } = useParams()
    const { colors } = useContextState()
    const styles = useStyles()
    const { t } = useTranslation()
    
    const statusLocale = getStatus((data.status == Status.CLOSE && isStore) ? Status.CANCEL : data.status, colors, t)
    const closeQuestionnaire = useMemo(() => [Status.TO_BE_STARTED, Status.STARTED, Status.PAUSE, Status.EXPIRED, Status.INCOMPLETE].includes(bodySection.status), [bodySection.status])
    const emits = useMemo(() => ({
        success: ({ message }: { message: string }) => { emitEvent(Events.snackbar, { open: true, type: Types.SUCCESS, message }) },
        error: ({ message }: { message: string }) => { emitEvent(Events.snackbar, { open: true, type: Types.ERROR, message }) },
    }), [])
    const closeSchedule = useMutation({
        mutationFn: userScheduleServiceFetch.closeUserSchedule,
        onSuccess: (data) => {
            if (data?.errors?.length) {
                emits.error({ message: t('schedule.closeQuestionnaire.errorSchedule') })
                return
            }
            queryClient.invalidateQueries({
                queryKey: [EventQuery.user_schedule],
                type: 'active',
            })
            emits.success({ message: t('schedule.closeQuestionnaire.successSchedule') })
        },
        onError: () => { emits.error({ message: t('schedule.closeQuestionnaire.errorSchedule') }) }
    })
    const closeScheduleQuestionnaire = ({ id }: { id?: string }) => {
        emitEvent(Events.modal, {
            open: true,
            title: t('schedule.modal.wantCloseSchedule'),
            description: t('schedule.modal.descriptionCloseSchedule'),
            cancel: (payload) => {
                payload?.close()
            },
            confirm: (payload) => {
                if (id) {
                    closeSchedule.mutate({ id })
                }
                payload?.close()
            },
        })
    }

    return (
        <>
            <Hidden xlUp>
                <ActionDrawer
                    body={(
                        <BosySecton {...bodySection} saveReportUrl={saveReportUrl} />
                    )}
                >
                    {({ openDrawer }) => (
                        <Visite
                            {...data}
                            detail
                            children={(
                                <IconButton onClick={openDrawer}>{icons('setting')}</IconButton>
                            )}
                            visitList={visitList}
                            seeDetail={seeDetail}
                        />
                    )}
                </ActionDrawer>
            </Hidden>

            <Hidden xlDown><Visite
                {...data} detail visitList={visitList} seeDetail={seeDetail} /></Hidden>
            <Box>
                <CustomeTab
                    listTab={[{ id: '1', label: t('schedule.detail.questionnaireExecution'), value: '1' }]}
                    value="1"
                    endChildren={(
                        <Box sx={styles.setting}>
                            <StatusComponent color={statusLocale.color} msg={statusLocale.text} />

                            {(!!closeQuestionnaire && bodySection?.typeOfQuestionnaire == TypeOfQuestionarie.BYUSER) && <LoadingButton loading={closeSchedule.isPending} onClick={() => { closeScheduleQuestionnaire({ id }) }} variant="container_error" color="error">{t('general.closeScheduleUser')}</LoadingButton>}
                        </Box>
                    )}
                >
                    <CustomePanel value="1">
                        {(!questionsData || questionsData.length === 0) && (<Loader />)}

                        {questionsData?.map((question, index) => (
                            <Section
                                key={index}
                                amountQuestions={question.amountQuestions}
                                description={question.description}
                                index={index}
                                totalPoints={question.totalPoints}
                                pointsOfUser={question.pointsOfUser}
                                questions={question.questions}
                                title={question.title}
                                value={question.value}
                                sectionOf={index + 1}
                                totalSection={questionsData.length}
                                statusId={question.statusId}
                            />
                        ))}
                    </CustomePanel>
                </CustomeTab>
            </Box>
        </>
    )
}

export { DetailVisit }
